'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { FormErrorBox } from '../FormErrorBox';
import { invalidLocations } from '../validation';
import type { LocationProblem } from '../validation';
import { LocationPicker } from './LocationPicker';
import type { StepProps } from './stepProps';
import { StepShell } from './StepShell';

const ALTERNATIVE_COUNT = 3;

/**
 * Step 2 — Standortauswahl (AC 3). The desired Standort is required; up to
 * three alternatives are optional. Order is preserved all the way to the
 * NHL-Tool's `schoolnumbers`.
 */
export const Step2LocationSelection = ({
  state,
  dispatch,
  chrome,
  onBack,
  onNext,
}: StepProps) => {
  const t = useTranslations('tutorApplication');
  // Every offending slot is tracked, not just the first: "Weiter" marks all
  // the inputs the applicant has to fix at once, rather than surfacing them
  // one per attempt.
  const [problems, setProblems] = useState<LocationProblem[]>([]);
  const errorFor = (index: number): string | undefined => {
    const problem = problems.find((candidate) => candidate.index === index);
    return problem ? t(problem.key) : undefined;
  };
  // Both keys can be present at once — an empty first slot and a third slot
  // holding junk — so the box lists each message once, in slot order.
  const messages = [...new Set(problems.map((problem) => problem.key))].map(
    (key) => t(key),
  );

  const handleNext = () => {
    const found = invalidLocations(state);
    setProblems(found);
    if (found.length === 0) {
      onNext();
    }
  };

  const setSlug = (
    index: number,
    slug: string | null,
    label: string,
    centerNumber: number | null,
  ) => {
    const slugs = [...state.locationSlugs];
    const labels = [...state.locationLabels];
    const centerNumbers = [...state.locationCenterNumbers];
    slugs[index] = slug;
    labels[index] = label;
    centerNumbers[index] = centerNumber;
    dispatch({ type: 'UPDATE_LOCATIONS', slugs, labels, centerNumbers });
    // Drop only this slot's message as soon as it is resolved or emptied;
    // leaving it up while the applicant types reads as a live error, and the
    // other slots are still wrong.
    setProblems((previous) =>
      previous.some((problem) => problem.index === index)
        ? previous.filter((problem) => problem.index !== index)
        : previous,
    );
  };

  return (
    <StepShell
      heading={t('step2Heading')}
      chrome={chrome}
      onBack={onBack}
      onNext={handleNext}
      hasError={messages.length > 0}
    >
      {messages.length > 0 && (
        <FormErrorBox id="application-location-error">
          {messages.map((message) => (
            <p key={message} className="m-0">
              {message}
            </p>
          ))}
        </FormErrorBox>
      )}

      {state.locationFixed ? (
        // The page's own Standort (Standort-Jobs Page): fixed, not editable.
        <p className="text-base">
          {t('selectedLocation', { name: state.locationLabels[0] ?? '' })}
        </p>
      ) : (
        <>
          <p className="text-base">{t('locationQuestion')}</p>

          <LocationPicker
            label={state.locationLabels[0]}
            onChange={(slug, label, centerNumber) =>
              setSlug(0, slug, label, centerNumber)
            }
            required
            error={errorFor(0)}
            errorId="application-location-error"
          />
        </>
      )}

      <p className="text-base">{t('locationAlternatives')}</p>

      {Array.from({ length: ALTERNATIVE_COUNT }, (_, offset) => {
        const index = offset + 1;
        return (
          <LocationPicker
            key={index}
            label={state.locationLabels[index]}
            onChange={(slug, label, centerNumber) =>
              setSlug(index, slug, label, centerNumber)
            }
            error={errorFor(index)}
            errorId="application-location-error"
          />
        );
      })}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={state.interestedInOnline}
          onChange={(e) =>
            dispatch({
              type: 'UPDATE_ONLINE_INTEREST',
              interested: e.target.checked,
            })
          }
        />
        <span className="text-sm">{t('onlineInterest')}</span>
      </label>
    </StepShell>
  );
};
